import React, { useState, useEffect, useRef } from "react";
import { LOG_CONFIGS, DEFAULT_INVOICE_DATA } from "../Constants";
import Loader from "../../utils/Loader";
import { formatOldUrl } from "../../utils/cloudApi";
import ImagePickerModal from "../../utils/ImagePicker";
import { useUser } from "../../contexts/UserContext";
import { useAdminContext } from "../../contexts/AdminContext";

const EditLogsTemplateModal = ({
  template,
  logType,
  isOpen,
  onClose,
  onSave,
}) => {
  const { user, getRecipients, privateKeyInMemory } = useUser();
  const { isViewingOtherUser, selectedUserData } = useAdminContext();
  const config = LOG_CONFIGS[logType];
  const [recordType, setRecordType] = useState("log");
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [errors, setErrors] = useState({});
  const [from, setFrom] = useState(DEFAULT_INVOICE_DATA.from);
  const [currentImageField, setCurrentImageField] = useState(null);
  const [logo, setLogo] = useState("");
  const [footer, setFooter] = useState("");
  const [logoDims, setLogoDims] = useState({
    width: 0,
    height: 0,
  });
  const [footerDims, setFooterDims] = useState({
    width: 0,
    height: 0,
  });
  const logoInputRef = useRef(null);
  const footerInputRef = useRef(null);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [previews, setPreviews] = useState({
    logoPreview: null,
    footerPreview: null,
  });
  const currentUser = isViewingOtherUser ? selectedUserData : user;
  const logTitles = {
    "secretarial-records": "Secretarial Records",
    "exec-admin": "Executive & Administrative",
    "process-server-logs": "Process Server & Messenger Logs",
    "case-brief": "Case Brief",
  };

  const logTitle = logTitles[logType] || "";

  useEffect(() => {
    if (template) {
      setTemplateName(template.name || "");
      setFrom(template?.from || { name: "", address: "", contact: "" });
      setLogo(template?.logo);
      setFooter(template?.footer);
      setRecordType(template?.recordType || "log");
      setPreviews((prev) => ({ ...prev, logoPreview: template?.logo }));
    } else {
      setTemplateName("");
      setFrom(
        DEFAULT_INVOICE_DATA?.from || { name: "", address: "", contact: "" }
      );
      setPreviews((prev) => ({ ...prev, logoPreview: template?.logo }));
      setLogo(null);
      setFooter(null);
      setRecordType("log");
    }
  }, [template, config]);

  const handleImageFieldClick = (field) => {
    setCurrentImageField(field);
    setImagePickerOpen(true);
  };

  const handleImageSelect = (selectedValue) => {
    const isFile = selectedValue instanceof File;

    if (isFile) {
      // New file upload
      const previewUrl = URL.createObjectURL(selectedValue);

      if (currentImageField === "logo") {
        if (previews.logoPreview?.startsWith("blob:")) {
          URL.revokeObjectURL(previews.logoPreview);
        }
        setPreviews((prev) => ({ ...prev, logoPreview: previewUrl }));
        setLogo(selectedValue);
      } else if (currentImageField === "footer") {
        if (previews.footerPreview?.startsWith("blob:")) {
          URL.revokeObjectURL(previews.footerPreview);
        }
        setPreviews((prev) => ({ ...prev, footerPreview: previewUrl }));
        setFooter(selectedValue);
      }
    } else {
      if (currentImageField === "logo") {
        setPreviews((prev) => ({ ...prev, logoPreview: selectedValue }));
        setLogo(selectedValue);
      } else if (currentImageField === "footer") {
        setPreviews((prev) => ({ ...prev, footerPreview: selectedValue }));
        setFooter(selectedValue);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!templateName.trim()) {
      newErrors.templateName = "Template name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setLoadingText("Saving template...");

    try {
      const updatedTemplate = {
        id: template?.id || `template_${Date.now()}`,
        name: templateName,
        from: from,
        logo: logo,
        footer: footer,
        logType: logType,
        recordType: recordType,
        createdAt: template?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      onSave(updatedTemplate);
      onClose();
    } catch (error) {
      console.error("Error saving template:", error);
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 py-6 flex items-center justify-center z-40">
      {loading && <Loader text={loadingText} />}

      {imagePickerOpen && (
        <ImagePickerModal
          isOpen={imagePickerOpen}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleImageSelect}
          userId={currentUser.uid}
          currentImageUrl={currentImageField === "logo" ? logo : footer}
        />
      )}

      <div className="bg-tier-primary dark:bg-tier-primary-dark dark:border-gray-600 border text-color rounded-lg shadow-xl w-full max-w-4xl h-full flex flex-col">
        <h2 className="text-2xl font-bold flex items-center gap-2 px-8 pt-8">
          {template ? "Edit" : "New"} {logTitle} Template
        </h2>
        <div className="custom-divider"></div>

        <div className="flex-1 overflow-y-auto p-8 space-y-6">
          {/* Template Name */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-color mb-2">
              Template Name
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
                if (errors.templateName) {
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.templateName;
                    return newErrors;
                  });
                }
              }}
              className={`custom-input ${
                errors.templateName ? "border-red-500" : ""
              }`}
              placeholder="e.g., Weekly Report Template, Daily Tasks Template"
            />
            {errors.templateName && (
              <p className="text-red-500 text-xs mt-1">{errors.templateName}</p>
            )}
            <p className="text-xs text-alt-color mt-1">
              Give your template a descriptive name to easily identify it later
            </p>
          </div>

          {/* Company Logo */}
          <div className="flex items-start gap-6 bg-white/10 p-4 rounded-lg">
            <div className="flex-1">
              <label htmlFor="logo" className="block text-sm">
                Company Logo
              </label>
              <button
                type="button"
                onClick={() => handleImageFieldClick("logo")}
                className="w-full p-3 border rounded-lg custom-input text-left hover:bg-white/5"
              >
                {logo ? "Change Logo Image" : "Select Logo Image"}
              </button>

              {logoDims.width > 0 && (
                <p className="mt-1 text-xs text-center text-gray-200">
                  Dimensions: {logoDims.width}px × {logoDims.height}
                  px
                </p>
              )}
            </div>
            {(previews.logoPreview || logo) && (
              <div className="flex flex-col items-center gap-2">
                <img
                  src={previews.logoPreview || formatOldUrl(logo)}
                  alt="Company Logo Preview"
                  className="max-h-32 h-auto max-w-sm border dark:border-gray-600 rounded-lg shadow-md"
                  onLoad={(e) =>
                    setLogoDims({
                      width: e.currentTarget.naturalWidth,
                      height: e.currentTarget.naturalHeight,
                    })
                  }
                />

                <button
                  type="button"
                  onClick={() => {
                    setPreviews((prev) => ({ ...prev, logoPreview: null }));
                    setLogo(null);
                    if (logoInputRef.current) {
                      logoInputRef.current.value = "";
                    }
                    setLogoDims({
                      width: 0,
                      height: 0,
                    });
                  }}
                  className="bg-red-400 dark:bg-red-500 text-color text-sm px-2 py-1 rounded hover:bg-red-500 dark:hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* Footer Banner */}
          <div className="flex items-start gap-6 bg-white/10 p-4 rounded-lg">
            <div className="flex-1">
              <label htmlFor="footer" className="block text-sm">
                Footer Banner
              </label>
              <button
                type="button"
                onClick={() => handleImageFieldClick("footer")}
                className="w-full p-3 border rounded-lg custom-input text-left hover:bg-white/5"
              >
                {footer ? "Change Footer Image" : "Select Footer Image"}
              </button>

              {footerDims.width > 0 && (
                <p className="mt-1 text-xs text-center text-gray-200">
                  Dimensions: {footerDims.width}px × {footerDims.height}px
                </p>
              )}
            </div>
            {(previews.footerPreview || footer) && (
              <div className="flex flex-col items-center gap-2">
                <img
                  src={previews.footerPreview || formatOldUrl(footer)}
                  alt="Footer Preview"
                  className="max-h-32 h-auto max-w-sm border dark:border-gray-600 rounded-lg shadow-md"
                  onLoad={(e) =>
                    setFooterDims({
                      width: e.currentTarget.naturalWidth,
                      height: e.currentTarget.naturalHeight,
                    })
                  }
                />

                <button
                  type="button"
                  onClick={() => {
                    if (previews.footerPreview?.startsWith("blob:")) {
                      URL.revokeObjectURL(previews.footerPreview);
                    }
                    setPreviews((prev) => ({ ...prev, footerPreview: null }));
                    setFooter(null);
                    if (footerInputRef.current) {
                      footerInputRef.current.value = "";
                    }
                    setFooterDims({ width: 0, height: 0 });
                  }}
                  className="bg-red-400 dark:bg-red-500 text-color text-sm px-2 py-1 rounded hover:bg-red-500 dark:hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* Document Type Selection - Only for secretarial-records and exec-admin */}
          {(logType === "secretarial-records" ||
            logType === "exec-admin" ||
            logType === "case-brief") && (
            <div className="bg-white/10 p-4 rounded-lg space-y-4">
              <label className="block text-sm font-semibold text-color mb-2">
                Select a Document Type
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setRecordType("log")}
                  className={`flex-1 flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
                    recordType === "log"
                      ? "border-teal-500 bg-teal-900/20"
                      : "border-gray-400 dark:border-gray-600 hover:border-teal-500 hover:bg-white/5"
                  }`}
                >
                  <span className="material-symbols-outlined text-tier-secondary">
                    event_list
                  </span>
                  <div className="text-left">
                    <p className="font-semibold">Standard Log Type</p>
                    <p className="text-xs text-gray-400">
                      Standard log entry. Automatically formatted logs in table
                      layout upon PDF generation.
                    </p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setRecordType("nte")}
                  className={`flex-1 flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
                    recordType === "nte"
                      ? "border-amber-500 bg-amber-900/20"
                      : "border-gray-400 dark:border-gray-600 hover:border-amber-500 hover:bg-white/5"
                  }`}
                >
                  <span className="material-symbols-outlined text-tier-secondary">
                    docs
                  </span>
                  <div className="text-left">
                    <p className="font-semibold">Letter Format</p>
                    <p className="text-xs text-gray-400">
                      Generates a PDF in formal letter format. Ex. Notice to
                      Explain
                    </p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Company Information */}
          <div className="bg-white/10 p-4 rounded-lg space-y-4">
            <h3 className="font-bold text-lg">Issuing Company</h3>

            <div className="flex flex-row w-full space-x-2">
              <div className="flex-1">
                <label htmlFor="from-name" className="block text-sm">
                  Company Name
                </label>
                <input
                  id="from-name"
                  type="text"
                  value={from.name}
                  onChange={(e) =>
                    setFrom((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full p-2 border rounded-lg custom-input"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="from-contact" className="block text-sm">
                  Contact
                </label>
                <input
                  id="from-contact"
                  type="text"
                  value={from.contact}
                  onChange={(e) =>
                    setFrom((prev) => ({ ...prev, contact: e.target.value }))
                  }
                  className="w-full p-2 border rounded-lg custom-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="from-address" className="block text-sm">
                Address
              </label>
              <input
                id="from-address"
                type="text"
                value={from.address}
                onChange={(e) =>
                  setFrom((prev) => ({ ...prev, address: e.target.value }))
                }
                className="w-full p-2 border rounded-lg custom-input"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 p-4 border-t border-gray-300 dark:border-gray-600 bg-tier-primary dark:bg-tier-primary-dark">
          <button onClick={onClose} className="btn-action-white">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-action">
            <span className="material-symbols-outlined text-sm mr-1">save</span>
            Save Template
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditLogsTemplateModal;
